import { ArrowLeft, BellRing, Pencil } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { PtsBillingService, PtsProjectService } from "@/services/APIs";
import PTSNavigationRoutes from "../../../utils/navigationRoutes";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { resolveProjectLabel } from "./billingListUtils";
import {
  formatReminderAmount,
  getBillingConfigurationEditPath,
  getTomorrowDateKey,
  INVOICE_REMINDER_QUERY_KEY,
} from "./invoiceReminderUtils";
import { invoiceStatusBadge } from "./utils";

const REMINDER_TABLE_COLUMN_COUNT = 12;

const InvoiceRemindersPage = () => {
  const navigate = useNavigate();
  const tomorrowDateKey = getTomorrowDateKey();

  const {
    data: reminders = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: INVOICE_REMINDER_QUERY_KEY,
    queryFn: () => PtsBillingService.getInvoiceReminders(),
  });

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = projectOptions.map(option => ({
    value: option.value,
    label: option.label,
  }));

  const tomorrowCount = reminders.filter(
    reminder => reminder.nextReminderDate === tomorrowDateKey
  ).length;

  const handleOpenInConfiguration = (billingId: string) => {
    navigate(getBillingConfigurationEditPath(billingId));
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="text-primary hover:text-primary -ml-2 w-fit px-2"
          >
            <Link to={PTSNavigationRoutes.BILLING_INVOICE}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Billing Invoice
            </Link>
          </Button>
          <div>
            <h1 className="text-primary text-2xl font-bold">
              Invoice Reminders
            </h1>
            <p className="text-sm text-gray-500">
              View all upcoming invoice reminders and follow-up schedules
            </p>
          </div>
        </div>

        <Card className="w-full border border-amber-200 bg-gradient-to-r from-amber-50 to-white md:max-w-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-xl bg-amber-500/10 p-3">
              <BellRing className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Tomorrow Reminders
              </p>
              <p className="text-foreground text-2xl font-bold tabular-nums">
                {isLoading ? "..." : tomorrowCount}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">All Reminders</CardTitle>
          <CardDescription>
            {reminders.length} reminder{reminders.length === 1 ? "" : "s"}{" "}
            scheduled from the billing system
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto px-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-primary font-semibold">
                    Invoice #
                  </TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead>Next Reminder</TableHead>
                  <TableHead className="text-center">Reminder Days</TableHead>
                  <TableHead className="text-center">Grace Period</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={REMINDER_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Loading invoice reminders...
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={REMINDER_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Failed to load invoice reminders
                    </TableCell>
                  </TableRow>
                ) : reminders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={REMINDER_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      No invoice reminders found
                    </TableCell>
                  </TableRow>
                ) : (
                  reminders.map(reminder => {
                    const isTomorrow =
                      reminder.nextReminderDate === tomorrowDateKey;

                    return (
                      <TableRow
                        key={reminder.id}
                        className={
                          isTomorrow ? "bg-amber-50/70 hover:bg-amber-50" : ""
                        }
                      >
                        <TableCell className="text-primary font-semibold">
                          {reminder.invoiceNumber || "—"}
                        </TableCell>
                        <TableCell>{reminder.client}</TableCell>
                        <TableCell>
                          {resolveProjectLabel(
                            reminder.project,
                            projectSelectOptions
                          )}
                        </TableCell>
                        <TableCell className="text-blue-600">
                          {reminder.frequency}
                        </TableCell>
                        <TableCell>{reminder.billingPeriod}</TableCell>
                        <TableCell>{reminder.invoiceDate}</TableCell>
                        <TableCell className="font-medium text-amber-800">
                          {reminder.nextReminderDateDisplay}
                        </TableCell>
                        <TableCell className="text-center">
                          {reminder.reminderDays > 0
                            ? reminder.reminderDays
                            : "—"}
                        </TableCell>
                        <TableCell className="text-center">
                          {reminder.gracePeriod > 0
                            ? reminder.gracePeriod
                            : "—"}
                        </TableCell>
                        <TableCell className="text-center align-middle">
                          <div className="flex justify-center">
                            {invoiceStatusBadge(reminder.status)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatReminderAmount(reminder)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:text-primary gap-1.5"
                            onClick={() =>
                              handleOpenInConfiguration(reminder.id)
                            }
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceRemindersPage;
